/**
 * PostItPanel - Sistema de Post-its vinculados ao texto
 * 
 * Permite criar anotações que ficam fixadas em posições do texto.
 * Visual de post-it real com pin.
 */

import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';

export default function PostItPanel({
  notes = [],
  onAddNote,
  onDeleteNote,
  onClose,
  isOpen = false,
}) {
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddNote = () => {
    if (newNoteContent.trim()) {
      onAddNote?.(newNoteContent.trim());
      setNewNoteContent('');
      setIsAdding(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddNote();
    }
    if (e.key === 'Escape') {
      setIsAdding(false);
      setNewNoteContent('');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed right-4 top-20 z-50 w-72 animate-in slide-in-from-right-4 duration-200"
      data-testid="postit-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-stone-600 flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="4" y="6" width="14" height="14" rx="1" fill="#fef08a" stroke="#eab308" strokeWidth="1"/>
            <circle cx="11" cy="5" r="3" fill="#dc2626"/>
          </svg>
          Notas do Capítulo
        </h3>
        <button
          onClick={onClose}
          className="p-1 text-stone-400 hover:text-stone-600 rounded"
        >
          <X size={16} />
        </button>
      </div>

      {/* Notes list */}
      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
        {notes.length === 0 && !isAdding && (
          <p className="text-xs text-stone-400 italic text-center py-4">
            Nenhuma nota ainda. Clique em + para adicionar.
          </p>
        )}

        {notes.map((note, index) => (
          <PostItNote
            key={note.postit_id || note.id || index}
            content={note.content}
            onDelete={() => onDeleteNote?.(note.postit_id || note.id)}
          />
        ))}

        {/* Add note form */}
        {isAdding && (
          <div className="relative">
            <div
              className="bg-gradient-to-br from-yellow-100 to-yellow-200 rounded shadow-md p-3"
              style={{ transform: 'rotate(-0.5deg)' }}
            >
              <textarea
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escreva sua nota..."
                className="w-full bg-transparent text-sm text-amber-900 placeholder-amber-600/50 resize-none focus:outline-none"
                rows={3}
                autoFocus
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => {
                    setIsAdding(false);
                    setNewNoteContent('');
                  }}
                  className="text-xs text-amber-700 hover:text-amber-900"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddNote}
                  disabled={!newNoteContent.trim()}
                  className="text-xs font-medium text-amber-800 hover:text-amber-900 disabled:opacity-50"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add button */}
      {!isAdding && (
        <button
          onClick={() => setIsAdding(true)}
          className="mt-3 w-full flex items-center justify-center gap-2 py-2 text-sm text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
        >
          <Plus size={14} />
          Adicionar nota
        </button>
      )}
    </div>
  );
}

// Individual Post-it Note
function PostItNote({ content, onDelete }) {
  const [showDelete, setShowDelete] = useState(false);

  return (
    <div
      className="relative group"
      onMouseEnter={() => setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
    >
      {/* Pin */}
      <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
        <svg width="16" height="20" viewBox="0 0 24 28" fill="none">
          <circle cx="12" cy="6" r="5" fill="#dc2626" />
          <circle cx="10" cy="4" r="1.5" fill="white" opacity="0.4" />
          <line x1="12" y1="10" x2="12" y2="16" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>

      {/* Paper */}
      <div
        className="relative bg-gradient-to-br from-yellow-100 to-yellow-200 rounded shadow-md p-3 pt-4"
        style={{ transform: `rotate(${Math.random() * 2 - 1}deg)` }}
      >
        <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">
          {content}
        </p>

        {/* Delete button */}
        {showDelete && (
          <button
            onClick={onDelete}
            className="absolute top-1 right-1 p-1 text-amber-600 hover:text-red-600 transition-colors"
            title="Excluir nota"
          >
            <Trash2 size={12} />
          </button>
        )}

        {/* Folded corner */}
        <div
          className="absolute bottom-0 right-0 w-4 h-4"
          style={{
            background: 'linear-gradient(135deg, transparent 50%, #fde047 50%)',
          }}
        />
      </div>
    </div>
  );
}
